/*
 * File: DetailPagePanel.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: July 21, 2025 at 1:26 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useContext, useEffect, useMemo,
	useRef
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useDebouncedCallback, useWindowResize } from 'beautiful-react-hooks';

// Context
import AnimationContextBuilder from '../../context/AnimationContext';

// Utilities
import { formatNumber } from '../../../utilities/utilities';
import { renderPrimaryResource } from '../../utilities/resources';
import { generateShortFormContentPlaceholder } from '../../utilities/placeholder';
import { topCenterPosition } from '../../utilities/position';

// Store
import { toggleGroundingExercise } from '../../store/slices/groundingExercise/groundingExercise.slice';

// Components
import { Typography } from '../Typography';
import { OnlineUsersWidget } from '../OnlineUsersWidget';
import { LocalitySelector } from '../LocalitySelector';
import { RelatedModule } from './RelatedModule';

// Constants
import { AVAILABLE_I18N_NAMESPACES, DEFAULT_COUNTRY, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './DetailPagePanel.styles';


/**
 * Component
 */

export const DetailPagePanel = memo(({
	className, offset, source, sourceId, related, onRelatedClick, inDrawer
}) => {

	// Get context from navigation parent
	const {
		dispatch: contextDispatch,
	} = useContext(AnimationContextBuilder);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get platform metrics from hook
	const platformMetrics = useSelector((state) => state.metric.metrics);

	// Get resources manifest from hook
	const resourceManifest = useSelector((state) => state.resourceManifest.resources);

	// Get animation state
	const { reactionAnimationActive } = useSelector((state) => state.animation);

	// Create references for elements
	const isMounted = useRef(true);
	const reactionMetricRef = useRef();

	// Get actions from hooks
	const dispatch = useDispatch();

	// Get primary resource from manifest
	const primaryResource = useMemo(() => {
		if (resourceManifest && resourceManifest.length > 0) {
			const countryResource = resourceManifest.find((resource) => resource.reference_countries.includes(locale.countryId));
			if (countryResource) {
				return countryResource;
			}
			return resourceManifest.find((resource) => resource.reference_countries.includes(DEFAULT_COUNTRY)) || resourceManifest[0];
		}
		return null;
	}, [locale, resourceManifest]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Generate placeholder
	const relatedPlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);

	// Open grounding activity
	const openGroundingActivity = () => {

		// Toggle grounding exercise
		dispatch(toggleGroundingExercise(true));
	};

	// Handle debounce function for updating context
	const debounceUpdateContext = useDebouncedCallback(() => {
		if (isMounted.current && !inDrawer) {

			// Get ref parameters
			const reactionRect = reactionMetricRef?.current?.getBoundingClientRect();

			// Set new position values
			contextDispatch({
				type: 'SET_DESKTOP_REACTION_METRIC_POSITION',
				payload: {
					left: reactionRect?.left || 0,
					top: topCenterPosition(reactionRect)
				}
			});
		}
	}, [inDrawer], 200);

	// Handle actions on window resize
	useWindowResize(() => {

		// Update context with debounce
		debounceUpdateContext();
	});

	// Handle actions on component load
	useEffect(() => {

		// Initialize mounted state
		isMounted.current = true;

		// Update context with debounce
		debounceUpdateContext();

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current) {

					// Update context with debounce
					debounceUpdateContext();
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render primary resource
	const renderedPrimaryResource = useMemo(() => renderPrimaryResource(primaryResource, locale, t), [primaryResource, locale, t]);

	// Render component
	return (
		<S.Wrapper className={className} $offset={offset}>

			{/* Section - Related */}
			{(!related || related.length > 0) && (
				<S.RelatedSection>

					{/* Title */}
					<Typography tag="h5" weight="semibold">
						<Trans t={t} i18nKey="detailPagePanel.relatedTitle">
							Similar community content
						</Trans>
					</Typography>

					{/* Related Container */}
					<S.RelatedContainer>
						{related
							? related.map((mod) => (mod.question_id
								? (
									<RelatedModule
										key={mod.question_id}
										module={mod}
										onRelatedClick={onRelatedClick}
										source={source}
										sourceId={sourceId}
									/>
								) : null))
							: (
								[1, 2, 3].map((key) => (
									<S.RelatedPlaceholder className="shimmer shimmer-element" key={`placeholder-${key}`}>
										<Typography weight="regular" tag="p" variation="2">
											{relatedPlaceholder}
										</Typography>
									</S.RelatedPlaceholder>
								))
							)}
					</S.RelatedContainer>

				</S.RelatedSection>
			)}

			{/* Section - Metrics */}
			<S.MetricsSection>

				{/* Metrics Container */}
				<S.MetricContainer>

					{/* Metric - Members */}
					<S.Metric>
						<Typography tag="p" weight="semibold" className={platformMetrics.community_members_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.community_members_count || 0)}</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.members">
								Members
							</Trans>
						</Typography>
					</S.Metric>

					{/* Metric - Views */}
					<S.Metric>
						<Typography tag="p" weight="semibold" className={platformMetrics.views_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.views_count || 0)}</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.views">
								Views
							</Trans>
						</Typography>
					</S.Metric>

				</S.MetricContainer>
				<S.MetricContainer>

					{/* Metric - Reactions */}
					<S.Metric>
						<Typography
							tag="p"
							weight="semibold"
							className={platformMetrics.reaction_a_count == null && 'shimmer shimmer-element'}
							style={{ display: 'flex', alignItems: 'center' }}
							ref={reactionMetricRef}
						>
							{formatNumber(platformMetrics.reaction_a_count || 0)}
							{platformMetrics.reaction_a_count != null && (
								<S.ReactionBadge
									icon={['fas', 'heart']}
									className={reactionAnimationActive && !inDrawer ? 'animate animatePulse' : 'animate'}
								/>
							)}
						</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.reactionA">
								Reactions
							</Trans>
						</Typography>
					</S.Metric>

					{/* Metric - Reads */}
					<S.Metric>
						<Typography tag="p" weight="semibold" className={platformMetrics.story_views_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.story_views_count || 0)}</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.storiesRead">
								Stories read
							</Trans>
						</Typography>
					</S.Metric>

				</S.MetricContainer>

				{/* Online Users */}
				<OnlineUsersWidget actionText={t('communityPanel.liveUsersStories')} />

			</S.MetricsSection>

			{/* Section - Grounding Activity */}
			{/* <S.ContentSection>
				<Typography tag="h5" weight="semibold">
					<Trans t={t} i18nKey="communityPanel.groundingPrompt">
						Need to take a break?
					</Trans>
				</Typography>
				<Button
					variant="solid"
					variation="secondary"
					size="medium"
					style={{ margin: '10px 0px 0px' }}
					onClick={openGroundingActivity}
				>
					<Typography tag="span" weight="semibold">
						<Trans t={t} i18nKey="communityPanel.groundingButton">
							Try a grounding activity
						</Trans>
					</Typography>
				</Button>
			</S.ContentSection> */}

			{/* Section - Resources */}
			<S.ResourcesSection>

				{/* Primary Resource */}
				<S.PrimaryResource>
					{renderedPrimaryResource}
				</S.PrimaryResource>

				{/* Locality Selector */}
				<LocalitySelector forceCompact />

			</S.ResourcesSection>
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

DetailPagePanel.displayName = 'DetailPagePanel';
DetailPagePanel.propTypes = {
	className: PropTypes.string,
	offset: PropTypes.number,
	source: PropTypes.oneOf(Object.values(SOURCES)),
	sourceId: PropTypes.string,
	related: PropTypes.arrayOf(PropTypes.shape()),
	onRelatedClick: PropTypes.func,
	inDrawer: PropTypes.bool
};
DetailPagePanel.defaultProps = {
	className: null,
	offset: 0,
	source: null,
	sourceId: null,
	related: null,
	onRelatedClick: null,
	inDrawer: false
};
